import {
    Component,
    computed,
    effect,
    inject,
    input,
    OnDestroy,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    AccountView,
    BuildingContactRoleView,
    CompanyView,
    DivisionView,
    OpportunityStatusView,
    QuickSearchAccountParameters,
    QuickSearchCompaniesParameters,
    SearchDivisionsParameters,
    SearchUsersParameters,
    UserView
} from '@api-proxies';
import { AccountService } from '@http/clients/account.service';
import { BuildingContactRoleService } from '@http/clients/building-contact-role.service';
import { CompanyService } from '@http/clients/company.service';
import { DepartmentService } from '@http/clients/department.service';
import { OpportunityStatusService } from '@http/clients/opportunity-status.service';
import { UserService } from '@http/clients/user.service';
import { FilterStateService } from '@services/filter-state.service';
import { FilterService, IGetById } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { AdditionalFiltersComponent } from '../additional-filters/additional-filters.component';
import {
    IAdditionalFiltersConfig,
    IAdditionalFilterVisibilityConfig,
    IReadonlyFields
} from '../additional-filters/additional-filters.config';
import { ITypeAheadService } from '../generic-type-ahead/generic-type-ahead.component';
import { OpportunitiesMainFiltersComponent } from './opportunities-main-filters/opportunities-main-filters.component';
import {
    FilterValueOpportunities,
    IAdditionalFiltersOpportunities,
    IMainFiltersOpportunities,
    OPPORTUNITIES_FILTER_CONFIG
} from './opportunity-filters.config';

@Component({
    selector: 'app-opportunities-filters',
    templateUrl: './opportunities-filters.component.html',
    styleUrls: ['./opportunities-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [OpportunitiesMainFiltersComponent, AdditionalFiltersComponent]
})
export class OpportunitiesFiltersComponent implements OnInit, OnDestroy {
    @ViewChild(AdditionalFiltersComponent)
    additionalFiltersComponent?: AdditionalFiltersComponent;

    currentTab = input<string | undefined>();

    filtersKey = input<string | undefined>();

    additionalFiltersVisibility = input<IAdditionalFilterVisibilityConfig>(OPPORTUNITIES_FILTER_CONFIG.visibility);

    additionalFiltersConfig = computed<IAdditionalFiltersConfig<IAdditionalFiltersOpportunities>>(() => ({
        ...OPPORTUNITIES_FILTER_CONFIG,
        visibility: this.additionalFiltersVisibility()
    }));

    readonlyFields = computed<IReadonlyFields>(() => {
        if (this.currentTab() === 'Calendar') {
            return { dueDate: true };
        }
        return {};
    });

    disabledClearFiltersBtn = true;
    companies?: CompanyView[];
    departments?: DivisionView[];
    statuses?: OpportunityStatusView[];
    companiesServiceFilter?: ITypeAheadService<CompanyView, QuickSearchCompaniesParameters>;
    userServiceFilter?: ITypeAheadService<UserView, SearchUsersParameters>;
    accountServiceFilter?: ITypeAheadService<AccountView, QuickSearchAccountParameters>;

    private filters: FilterValueOpportunities = {} as FilterValueOpportunities;
    private filterSubscription?: Subscription;

    private readonly filterStateService = inject(
        FilterStateService<IMainFiltersOpportunities, IAdditionalFiltersOpportunities>
    );
    private readonly filterService = inject(FilterService);
    private readonly opportunityStatusService = inject(OpportunityStatusService);
    private readonly departmentService = inject(DepartmentService);
    private readonly companyService = inject(CompanyService);
    private readonly companyRolesService = inject(BuildingContactRoleService);
    private readonly userService = inject(UserService);
    private readonly accountService = inject(AccountService);
    private readonly activatedRoute = inject(ActivatedRoute);

    constructor() {
        effect(() => {
            const tab = this.currentTab();
            if (tab === 'Calendar') {
                const currentFilters = this.filterStateService.current;
                if (currentFilters && (currentFilters.dueDateFrom || currentFilters.dueDateTo)) {
                    this.filterStateService.updateAdditionalFilters({
                        ...currentFilters,
                        dueDateFrom: undefined,
                        dueDateTo: undefined
                    });
                }
            }
        });
    }

    async ngOnInit(): Promise<void> {
        this.companiesServiceFilter = this.companyService;
        this.userServiceFilter = this.userService;
        this.accountServiceFilter = this.accountService;
        this.statuses = await this.opportunityStatusService.getAll();
        this.departments = await this.departmentService.search({ isActive: undefined } as SearchDivisionsParameters);

        await this.deserializeUrlParams();

        const hasUrlFilters = Object.keys(this.activatedRoute.snapshot.queryParams).length > 0;
        const filtersKey = this.filtersKey() || '';
        if (filtersKey && !hasUrlFilters) {
            const filtersFromLocalStorage = this.filterStateService.getFromLocalStorage(
                filtersKey
            ) as FilterValueOpportunities;

            if (filtersFromLocalStorage) {
                this.filters = { ...this.filters, ...filtersFromLocalStorage };
            }
        }

        this.filterStateService.updateFilters(this.filters);

        this.filterSubscription = this.filterStateService.stateFilters$.subscribe(async (newFilters) => {
            if (!newFilters) return;

            this.filters = { ...newFilters };
            await this.updateFilterUrlParams();
            this.disabledClearFiltersBtn = !this.someFilterApplied();
            if (filtersKey) {
                this.filterStateService.saveToLocalStorage(filtersKey, this.filters);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
    }

    async onClearAllFilters(): Promise<void> {
        this.filterStateService.clearAllFilters();

        if (this.additionalFiltersComponent) {
            this.additionalFiltersComponent.onClearFilters();
        }
    }

    private async updateFilterUrlParams(): Promise<void> {
        await this.filterService.serialize<FilterValueOpportunities>(this.filters, 'sortBy');
        await this.filterService.serialize<FilterValueOpportunities>(this.filters, 'sortDirection');
        await this.filterService.serialize<FilterValueOpportunities>(this.filters, 'searchText');
        await this.filterService.serialize<FilterValueOpportunities>(this.filters, 'onlyFavorites');
        await this.filterService.serializeDate<FilterValueOpportunities>(this.filters, 'dueDateFrom');
        await this.filterService.serializeDate<FilterValueOpportunities>(this.filters, 'dueDateTo');
        await this.filterService.serializeBoolean<FilterValueOpportunities>(this.filters, 'includeArchived');
        await this.filterService.serializeTypeAhead<FilterValueOpportunities, CompanyView>(
            'companies',
            this.filters.companies,
            'id',
            true
        );

        await this.filterService.serializeArray<FilterValueOpportunities, DivisionView>(
            this.filters,
            'departments',
            'id',
            true
        );

        await this.filterService.serializeTypeAhead<FilterValueOpportunities, UserView>(
            'salespersons',
            this.filters.salespersons,
            'externalId',
            true
        );
        await this.filterService.serializeArray<FilterValueOpportunities, OpportunityStatusView>(
            this.filters,
            'statuses',
            'id',
            true
        );
        await this.filterService.serialize<FilterValueOpportunities>(this.filters, 'probabilityFrom');
        await this.filterService.serialize<FilterValueOpportunities>(this.filters, 'probabilityTo');
        await this.filterService.serialize<FilterValueOpportunities>(this.filters, 'potentialValueFrom');
        await this.filterService.serialize<FilterValueOpportunities>(this.filters, 'potentialValueTo');
        await this.filterService.serialize<FilterValueOpportunities>(this.filters, 'proposedGrossMarginFrom');
        await this.filterService.serialize<FilterValueOpportunities>(this.filters, 'proposedGrossMarginTo');
        await this.filterService.serializeDate<FilterValueOpportunities>(this.filters, 'expectedResponseDateFrom');
        await this.filterService.serializeDate<FilterValueOpportunities>(this.filters, 'expectedResponseDateTo');
        await this.filterService.serializeTypeAhead<FilterValueOpportunities, AccountView>(
            'accounts',
            this.filters.accounts,
            'id',
            true
        );

        await this.filterService.serializeTypeAhead<FilterValueOpportunities, BuildingContactRoleView>(
            'companyRoles',
            this.filters.companyRoles,
            'id',
            true
        );
    }

    private async deserializeUrlParams(): Promise<void> {
        this.filterService.deserialize<FilterValueOpportunities>(this.filters, 'sortBy');
        this.filterService.deserialize<FilterValueOpportunities>(this.filters, 'sortDirection');
        this.filterService.deserialize<FilterValueOpportunities>(this.filters, 'searchText');
        this.filterService.deserializeBoolean<FilterValueOpportunities>(this.filters, 'onlyFavorites');
        this.filterService.deserializeDate<FilterValueOpportunities>(this.filters, 'dueDateFrom');
        this.filterService.deserializeDate<FilterValueOpportunities>(this.filters, 'dueDateTo');
        this.filterService.deserializeBoolean<FilterValueOpportunities>(this.filters, 'includeArchived');
        this.filterService.deserializeNumber<FilterValueOpportunities>(this.filters, 'probabilityFrom');
        this.filterService.deserializeNumber<FilterValueOpportunities>(this.filters, 'probabilityTo');
        this.filterService.deserializeNumber<FilterValueOpportunities>(this.filters, 'potentialValueFrom');
        this.filterService.deserializeNumber<FilterValueOpportunities>(this.filters, 'potentialValueTo');
        this.filterService.deserializeNumber<FilterValueOpportunities>(this.filters, 'proposedGrossMarginFrom');
        this.filterService.deserializeNumber<FilterValueOpportunities>(this.filters, 'proposedGrossMarginTo');
        await this.filterService.deserializeTypeAhead<IAdditionalFiltersOpportunities, CompanyView>(
            this.filters,
            'companies',
            this.companyService as IGetById,
            true
        );
        this.filterService.deserializeArray<IAdditionalFiltersOpportunities, DivisionView>(
            this.filters,
            'departments',
            'id',
            this.departments ?? [],
            true
        );

        await this.filterService.deserializeTypeAhead<IAdditionalFiltersOpportunities, UserView>(
            this.filters,
            'salespersons',
            this.userService as IGetById,
            true
        );
        this.filterService.deserializeArray<IAdditionalFiltersOpportunities, OpportunityStatusView>(
            this.filters,
            'statuses',
            'id',
            this.statuses ?? [],
            true
        );
        this.filterService.deserializeDate<IAdditionalFiltersOpportunities>(this.filters, 'expectedResponseDateFrom');
        this.filterService.deserializeDate<IAdditionalFiltersOpportunities>(this.filters, 'expectedResponseDateTo');

        await this.filterService.deserializeTypeAhead<IAdditionalFiltersOpportunities, AccountView>(
            this.filters,
            'accounts',
            this.accountService as IGetById,
            true
        );

        await this.filterService.deserializeTypeAhead<IAdditionalFiltersOpportunities, BuildingContactRoleView>(
            this.filters,
            'companyRoles',
            this.companyRolesService as IGetById,
            true
        );
    }

    private someFilterApplied(): boolean {
        return Object.values(this.filters).some((f) => (Array.isArray(f) ? f.length : f));
    }
}
